import React from "react";
import {
    SIDEBAR_COLLAPSE_EVENT,
    SIDEBAR_EXPAND_EVENT,
} from "~/components/SidebarLayout/events";

// // // //

// Set a tolerance level to ignore minor changes in height on mobile
// NOTE - this is necessary to handle erroneous window resize events in iOS devices.
// The issue of iOS triggering resize events on scrolling is a known problem.
// This is because of the way iOS Safari/Chrome handles the address bar and other
// UI elements, which can cause the inner height of the window
// to change during scroll events.
const HEIGHT_TOLERANCE = 100;

// How long resize events must be quiet before subscribers are told the resize ended
const RESIZE_END_DEBOUNCE_MS = 250;

// How long children stay hidden after the sidebar expands or collapses. The window
// itself doesn't resize, but the sidebar width animates and the page content reflows
// with it, so heavy children get a fixed quiet window before re-mounting at their new width.
const SIDEBAR_TOGGLE_HIDE_MS = 500;

interface WindowDimensions {
    windowWidth: number;
    windowHeight: number;
}

// What caused the children to be hidden - a real window resize, or the sidebar toggling
type HideSource = "resize" | "sidebar";

interface ResizeSubscriber {
    onResizeStart: () => void;
    onResizeEnd: (dimensions: WindowDimensions, source: HideSource) => void;
}

// Single shared window listener for every WindowResizeObserver on the page.
// Subscribers are notified synchronously from one place so React batches all
// of their state updates into a single commit, instead of each instance
// running its own debounce timers and committing at staggered times.
const subscribers = new Set<ResizeSubscriber>();
let isHidden = false;
let lastWidth = 0;
let lastHeight = 0;
let revealTimer: ReturnType<typeof setTimeout> | null = null;
let revealAt = 0;
let revealSource: HideSource = "resize";

// Leading edge: swap children for placeholders on the FIRST event of a hide,
// so they're gone before a drag continues (or the sidebar animation runs),
// rather than after a trailing debounce that keeps getting pushed back.
function hideChildren() {
    if (isHidden) {
        return;
    }
    isHidden = true;
    subscribers.forEach((subscriber) => subscriber.onResizeStart());
}

// Trailing edge: reveal children `ms` from now. Both hide sources share this one
// timer and the LATER deadline always wins, so a sidebar toggle's fixed window
// isn't cut short by a stray resize event mid-animation, and a resize that ends
// inside that window doesn't reveal early either. For resize events each call
// lands later than the last, so this still behaves as a plain debounce.
function scheduleReveal(ms: number, source: HideSource) {
    const at = Date.now() + ms;
    if (revealTimer && at <= revealAt) {
        return;
    }
    if (revealTimer) {
        clearTimeout(revealTimer);
    }
    revealAt = at;
    revealSource = source;
    revealTimer = setTimeout(() => {
        revealTimer = null;
        isHidden = false;
        subscribers.forEach((subscriber) =>
            subscriber.onResizeEnd(
                { windowWidth: lastWidth, windowHeight: lastHeight },
                revealSource,
            ),
        );
    }, ms);
}

function handleWindowResize() {
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight;

    // Short-circuit if window dimensions haven't changed significantly
    if (
        newWidth === lastWidth &&
        Math.abs(newHeight - lastHeight) < HEIGHT_TOLERANCE
    ) {
        return;
    }

    lastWidth = newWidth;
    lastHeight = newHeight;

    hideChildren();
    scheduleReveal(RESIZE_END_DEBOUNCE_MS, "resize");
}

// Fired by SidebarLayout's collapse button (see SidebarLayout/events.ts)
function handleSidebarToggle() {
    hideChildren();
    scheduleReveal(SIDEBAR_TOGGLE_HIDE_MS, "sidebar");
}

function subscribeToWindowResize(subscriber: ResizeSubscriber): () => void {
    if (subscribers.size === 0) {
        lastWidth = window.innerWidth;
        lastHeight = window.innerHeight;
        window.addEventListener("resize", handleWindowResize);
        window.addEventListener(SIDEBAR_EXPAND_EVENT, handleSidebarToggle);
        window.addEventListener(SIDEBAR_COLLAPSE_EVENT, handleSidebarToggle);
    }
    subscribers.add(subscriber);

    return () => {
        subscribers.delete(subscriber);
        if (subscribers.size === 0) {
            window.removeEventListener("resize", handleWindowResize);
            window.removeEventListener(
                SIDEBAR_EXPAND_EVENT,
                handleSidebarToggle,
            );
            window.removeEventListener(
                SIDEBAR_COLLAPSE_EVENT,
                handleSidebarToggle,
            );
            if (revealTimer) {
                clearTimeout(revealTimer);
                revealTimer = null;
            }
            isHidden = false;
        }
    };
}

// // // //

export interface WindowResizeObserverProps {
    debug?: boolean;
    defaultPlaceholderHeight?: number;
    delay?: number;
    loadingPlaceholder?: React.ReactNode | (() => React.ReactNode);
}

/**
 * WindowResizeObserver
 * Hides props.children behind a lightweight placeholder while the window is
 * being resized, and re-mounts them once resizing settles (plus optional delay).
 * Also hides them for a fixed window whenever the sidebar expands or collapses,
 * since the content area reflows even though the window itself doesn't resize.
 */
export function WindowResizeObserver(
    props: WindowResizeObserverProps & { children: React.ReactNode },
) {
    const { debug, delay = 0, loadingPlaceholder = null } = props;

    const [shouldRender, setShouldRender] = React.useState(true);
    const [dimensions, setDimensions] = React.useState<WindowDimensions>({
        windowWidth: 0,
        windowHeight: 0,
    });

    React.useEffect(() => {
        // Initial dimensions must be read post-mount: reading window during
        // render would produce SSR/hydration mismatches in the debug output
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDimensions({
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
        });

        let delayTimer: ReturnType<typeof setTimeout> | null = null;

        const unsubscribe = subscribeToWindowResize({
            onResizeStart: () => {
                if (delayTimer) {
                    clearTimeout(delayTimer);
                    delayTimer = null;
                }
                setShouldRender(false);
            },
            onResizeEnd: (newDimensions, source) => {
                const show = () => {
                    setDimensions(newDimensions);
                    setShouldRender(true);
                };
                // A sidebar toggle already holds the placeholder for a fixed
                // window, so the per-instance settle delay only applies to
                // real window resizes
                if (source === "resize" && delay > 0) {
                    delayTimer = setTimeout(show, delay);
                } else {
                    show();
                }
            },
        });

        return () => {
            unsubscribe();
            if (delayTimer) {
                clearTimeout(delayTimer);
            }
        };
    }, [delay]);

    let resizePlaceholderVal: React.ReactNode | null = null;

    if (
        loadingPlaceholder !== null &&
        typeof loadingPlaceholder === "function"
    ) {
        resizePlaceholderVal = loadingPlaceholder();
    } else if (loadingPlaceholder !== null) {
        resizePlaceholderVal = loadingPlaceholder;
    } else if (props.defaultPlaceholderHeight != null) {
        resizePlaceholderVal = (
            <div
                style={{
                    height: `${props.defaultPlaceholderHeight}px`,
                }}
            />
        );
    }

    return (
        <React.Fragment>
            {debug && (
                <pre>
                    {JSON.stringify(
                        {
                            windowWidth: dimensions.windowWidth,
                            windowHeight: dimensions.windowHeight,
                            shouldRender,
                        },
                        null,
                        4,
                    )}
                </pre>
            )}
            {shouldRender ? props.children : resizePlaceholderVal}
        </React.Fragment>
    );
}
